import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { LucideAngularModule, History, Search, Loader2, TriangleAlert, Download } from 'lucide-angular';
import { EventHistoryLookupService } from './event-history-lookup.service';
import { EventHistory } from './event-history.model';

/**
 * TVG-220: Admin-only Event History Lookup.
 *
 * A standalone page (no sidebar entry, reachable by direct URL only) where up to
 * 10 event references are entered by hand and the full history for each is
 * returned. The route is gated by the `event-history-lookup` feature toggle
 * (FeatureToggleGuard) and the admin role (AuthGuard), so the page only renders
 * where the toggle is enabled and never for non-admins.
 */
@Component({
  selector: 'app-event-history-lookup-page',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './event-history-lookup-page.component.html',
})
export class EventHistoryLookupPageComponent {
  protected readonly History = History;
  protected readonly Search = Search;
  protected readonly Loader2 = Loader2;
  protected readonly TriangleAlert = TriangleAlert;
  protected readonly Download = Download;

  /** The maximum number of references that can be looked up at once. */
  protected readonly maxReferences = 10;

  /** Raw textarea content — one reference per line (or comma separated). */
  protected rawReferences = '';

  protected histories: EventHistory[] = [];
  protected notFound: string[] = [];
  protected loading = false;
  protected exporting = false;
  protected errorMessage: string | null = null;
  protected hasSearched = false;

  /** References parsed from the textarea, trimmed and de-duplicated. */
  protected get parsedReferences(): string[] {
    const seen = new Set<string>();
    const references: string[] = [];

    for (const token of this.rawReferences.split(/[\n,]+/)) {
      const trimmed = token.trim();
      if (trimmed === '') {
        continue;
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      references.push(trimmed);
    }

    return references;
  }

  protected get tooManyReferences(): boolean {
    return this.parsedReferences.length > this.maxReferences;
  }

  protected get canSubmit(): boolean {
    return !this.loading && this.parsedReferences.length > 0 && !this.tooManyReferences;
  }

  protected submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.hasSearched = true;

    this.service.lookup(this.parsedReferences).subscribe({
      next: (response) => {
        this.histories = response.data;
        this.notFound = response.not_found;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.histories = [];
        this.notFound = [];
        this.loading = false;
        this.errorMessage =
          error.error?.message ?? 'Something went wrong looking up those references. Please try again.';
      },
    });
  }

  protected get canExport(): boolean {
    return !this.exporting && this.histories.length > 0;
  }

  /**
   * TVG-222: Download the results currently on screen as a CSV. The references of
   * the resolved histories are re-sent so the file covers exactly those records.
   */
  protected exportCsv(): void {
    if (!this.canExport) {
      return;
    }

    this.exporting = true;

    this.service.export(this.histories.map((history) => history.reference)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `event-history-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: () => {
        this.errorMessage = 'Something went wrong exporting the results. Please try again.';
        this.exporting = false;
      },
    });
  }

  constructor(private readonly service: EventHistoryLookupService) {}
}
